import { Controller } from "@hotwired/stimulus"
import { BusyDelay } from "../lib/busy_delay.mjs"
import { isInteractiveFrameRequest } from "../lib/turbo_request.mjs"
import { FrameBusyLifecycle } from "../lib/frame_busy_lifecycle.mjs"
import { visibleFrameCenter } from "../lib/frame_feedback_position.mjs"

// Stale-while-loading pagination for Turbo Frames. Turbo owns the request and applies its
// immediate `busy`/`aria-busy`; this controller adds delayed visual feedback, pager focus and
// a controlled retry while preserving ordinary links when JavaScript is unavailable.
export default class extends Controller {
  static targets = ["feedback", "indicator", "error", "retry", "announcement"]

  connect() {
    this.lifecycle = new FrameBusyLifecycle()
    this.delay = new BusyDelay({
      show: () => this.indicatorTarget.classList.remove("hidden"),
      hide: () => this.indicatorTarget.classList.add("hidden")
    })
    this.reposition = () => this.#schedulePosition()
    this.reset()
  }

  disconnect() {
    this.reset()
  }

  prepare(event) {
    const link = event.target.closest("[data-pagination-link], [data-busy-link]")
    if (!link || !this.element.contains(link)) return
    if (link.dataset.turboFrame && link.dataset.turboFrame !== this.element.id) return
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    if (!this.lifecycle.prepare(link.href, { pager: link.matches("[data-pagination-link]") })) {
      event.preventDefault()
      return
    }

    this.pendingLink = link
    link.setAttribute("aria-disabled", "true")
    link.classList.add("pagination__link--busy")
    this.element.setAttribute("aria-busy", "true")
    this.#lockContent(true)
    this.delay.start()
    this.#hideError()
    this.#startPositioning()
  }

  start(event) {
    // Depending on the Turbo navigation path the request event may target the clicked link
    // rather than the Frame. `prepare` has already limited that case to our own busy links.
    if (!this.lifecycle.pending && !isInteractiveFrameRequest(event, this.element)) return

    this.lifecycle.start(event.detail.url?.toString())
    this.element.setAttribute("aria-busy", "true")
    this.#lockContent(true)
    this.delay.start()
    this.#startPositioning()
  }

  response(event) {
    if (!this.lifecycle.pending) return
    if (event.detail.fetchResponse.succeeded) {
      const contentType = event.detail.fetchResponse.response?.headers?.get("Content-Type") || ""
      // Turbo Stream mutations update a cell/card rather than replacing this results Frame, so
      // no turbo:frame-load will follow. End the local busy state before Turbo applies the stream.
      if (contentType.includes("turbo-stream")) {
        this.lifecycle.succeed()
        this.#finish()
        this.#hideError()
      }
      return
    }

    // A form inside a paginated Frame can deliberately return a complete 409/422 body.
    // Leave that response to Turbo; the generic retry state belongs only to prepared pager links.
    if (!this.pendingLink) {
      this.lifecycle.reset()
      this.#finish()
      return
    }

    event.preventDefault()
    this.error()
  }

  success(event) {
    if (event.target !== this.element) return
    const shouldFocus = this.lifecycle.succeed()
    this.#finish()
    this.#hideError()

    if (shouldFocus) {
      this.element.focus({ preventScroll: true })
      this.announcementTarget.textContent = this.announcementTarget.dataset.loadedMessage
    }
  }

  error() {
    if (!this.lifecycle.pending) return
    const retryURL = this.lifecycle.fail()
    this.#finish()
    if (!retryURL) return

    this.retryTarget.href = retryURL
    this.errorTarget.classList.remove("hidden")
    this.#startPositioning()
  }

  missing(event) {
    if (event.target !== this.element) return
    event.preventDefault()
    this.error()
  }

  cache() {
    // Turbo snapshots an `advance` Frame while its request is still live. Clean only the DOM
    // copied into history; keep request metadata so the live response can still focus/announce.
    const announcement = this.hasAnnouncementTarget ? this.announcementTarget.textContent : ""
    this.#finish()
    this.#hideError()
    if (this.hasAnnouncementTarget) this.announcementTarget.textContent = ""
    if (announcement) {
      queueMicrotask(() => {
        if (this.element.isConnected && this.hasAnnouncementTarget) {
          this.announcementTarget.textContent = announcement
        }
      })
    }
  }

  reset() {
    this.lifecycle?.reset()
    this.#finish()
    this.#hideError()
    if (this.hasAnnouncementTarget) this.announcementTarget.textContent = ""
  }

  #finish() {
    this.delay?.finish()
    this.#stopPositioning()
    this.pendingLink?.removeAttribute("aria-disabled")
    this.pendingLink?.classList.remove("pagination__link--busy")
    this.pendingLink = null
    this.element.removeAttribute("busy")
    this.element.removeAttribute("aria-busy")
    this.#lockContent(false)
  }

  #hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  #lockContent(locked) {
    for (const child of this.element.children) {
      if (child.matches(".busy-frame__feedback")) continue
      child.inert = locked
    }
  }

  #startPositioning() {
    if (!this.positioning) {
      this.positioning = true
      window.addEventListener("scroll", this.reposition, { passive: true })
      window.addEventListener("resize", this.reposition)
    }
    this.#schedulePosition()
  }

  #schedulePosition() {
    if (this.positionFrame) return

    this.positionFrame = window.requestAnimationFrame(() => {
      this.positionFrame = null
      const center = visibleFrameCenter(this.element.getBoundingClientRect(), window.innerHeight)
      this.feedbackTarget.style.setProperty("--busy-frame-visible-center", `${center}px`)
    })
  }

  #stopPositioning() {
    if (this.positioning) {
      window.removeEventListener("scroll", this.reposition)
      window.removeEventListener("resize", this.reposition)
      this.positioning = false
    }
    if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame)
    this.positionFrame = null
  }
}
